import React, { ReactElement } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Box, Card, Chip, IconButton, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { EditIcon } from 'src/v3/core/icons'
import { Event } from 'src/v3/core/types'
import { CalendarSession } from '../calendar/types'
import { getTimezoneDisplay } from '../calendar/utils/date-utils'

dayjs.extend(utc)
dayjs.extend(timezone)

type Instructor = {
  id: string
  name: string
  credentials?: string
}

type Props = {
  sessions: CalendarSession[]
  events: Event[]
  instructors: Instructor[]
  userTimeZone: string
  onEdit?: (session: CalendarSession) => void
  onDelete?: (sessionID: string) => void
  emptyLabel?: string
}

let formatBadgeLabel = (kind?: string): string => {
  if (kind === 'virtual') return 'Virtual'
  if (kind === 'inPerson') return 'Live in-person'
  if (kind === 'hybrid') return 'Hybrid'
  return ''
}

let locationText = (event: Event | undefined): string => {
  if (!event) return ''
  let { location } = event
  if (location.kind === 'virtual') return location.name
  if (location.kind === 'inPerson') return location.name || location.city
  if (location.kind === 'hybrid') return location.name || location.city
  return ''
}

export default function SessionListView({
  sessions,
  events,
  instructors,
  userTimeZone,
  onEdit,
  onDelete,
  emptyLabel = 'No upcoming sessions',
}: Props): ReactElement {
  if (sessions.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {emptyLabel}
        </Typography>
      </Card>
    )
  }

  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {sessions.map((session) => {
        let event = events.find((e) => e.id === session.eventID)
        let displayTime = getTimezoneDisplay(
          session.sessionTime,
          session.timeZone,
          userTimeZone,
        )
        let sessionDate = dayjs.utc(session.sessionTime).tz(userTimeZone)
        let instructorNames =
          session.instructorNames.length > 0
            ? session.instructorNames.join(', ')
            : session.instructorIDs
                .map((id) => instructors.find((i) => i.id === id)?.name)
                .filter(Boolean)
                .join(', ')
        let location = locationText(event)
        let badgeLabel = formatBadgeLabel(event?.location.kind)

        return (
          <Box component="li" key={session.id}>
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {event?.name ?? 'Unknown Event'}
                    {session.name && (
                      <Typography
                        component="span"
                        color="text.secondary"
                        variant="body2"
                        sx={{ ml: 1 }}
                      >
                        — {session.name}
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ display: 'block' }}
                  >
                    {sessionDate.format('MMM D, YYYY • ')}
                    {displayTime}
                    {location && ` • ${location}`}
                  </Typography>
                  {instructorNames && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {instructorNames}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {badgeLabel && (
                    <Chip label={badgeLabel} size="small" variant="outlined" />
                  )}
                  {onEdit && (
                    <IconButton
                      size="small"
                      onClick={() => onEdit(session)}
                      aria-label={`Edit session for ${event?.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(session.id)}
                      aria-label={`Delete session for ${event?.name}`}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Card>
          </Box>
        )
      })}
    </Box>
  )
}
